import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { isChannelNotFoundError } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import {
	unifiedTaskToApiSSE,
	useActiveTasksForConversations,
} from '@atlassian/conversation-assistant-store/controllers/unified-tasks';
import { useUnifiedTasksSSESubscription } from '@atlassian/conversation-assistant-store/controllers/unified-tasks/use-unified-tasks-sse-subscription';
import { ChatConversationListRefresh } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list-refresh/ChatConversationListRefresh';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { isEmptyConversation } from '../../controllers/chat-store/isEmptyConversation';
import { useChatStore } from '../../controllers/chat-store/useChatStore';

import messages from './messages';

type ConversationListProps = {
	chatRefreshHeader?: React.ReactNode;
};

export const ConversationList = ({
	chatRefreshHeader,
}: ConversationListProps): React.JSX.Element => {
	di(
		useGlobalConfig,
		useChatStore,
		useUIStoreActions,
		useUIStoreSubscription,
		useUIConfig,
		useStagingAreaActions,
		useAssistanceServiceParams,
		useUnifiedTasksSSESubscription,
		useActiveTasksForConversations,
		useAnalytics,
	);
	const { formatMessage } = useIntl();
	const { fullScreen } = useUIConfig();
	const { siteId } = useGlobalConfig();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'ConversationList',
	});

	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const { resetStagingArea } = useStagingAreaActions();
	const isPinnedChatsEnabled = fg('rovo_chat_pinned_fe');
	const [
		{ conversations: conversationsStore, conversationPagination },
		{
			setSelectedConversation,
			updateConversation,
			deleteConversation,
			loadMoreConversationChannelsData,
			setConversationListMounted,
		},
	] = useChatStore();

	const [searchQuery, setSearchQuery] = useState('');
	const { sendAnalyticsEvent } = useAnalytics();

	/** Live task rows need SSE only while this list is mounted (not at assistant root). */
	useUnifiedTasksSSESubscription({
		// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- enable_tasks is the umbrella gate for task updates.
		enabled: fg('rovo_chat_enable_tasks'),
		assistanceServiceParams,
	});

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoChatConversationHistory',
		});
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setConversationListMounted(true);

		return () => {
			setConversationListMounted(false);
		};
	}, [setConversationListMounted]);

	const visibleConvIds = useMemo(() => Object.keys(conversationsStore), [conversationsStore]);
	const [activeTasksByConvId] = useActiveTasksForConversations(visibleConvIds);

	const conversations = useMemo(
		() =>
			Object.values(conversationsStore).map((conversation) => {
				const activeTask = activeTasksByConvId.get(conversation.id);
				return {
					...conversation,
					currentTask: activeTask ? unifiedTaskToApiSSE(activeTask) : null,
					isEmpty: isEmptyConversation(conversation.id),
				};
			}),
		[conversationsStore, activeTasksByConvId],
	);

	const onNewChatClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.preventDefault();

			if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				resetStagingArea();
			}
			setSelectedConversation({
				conversationId: EMPTY_CONVERSATION_ID,
			});
			setActiveMenu('none');
		},
		[resetStagingArea, setActiveMenu, setSelectedConversation],
	);

	const onConversationSelect = useCallback(
		(conversationId: string) => {
			if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				resetStagingArea();
			}
			setSelectedConversation({ conversationId });
			setActiveMenu('none');
		},
		[resetStagingArea, setActiveMenu, setSelectedConversation],
	);

	const onConversationEdit = useCallback(
		async (conversationId: string, newName: string) => {
			try {
				await updateConversation({
					conversationId,
					data: {
						name: newName.trim(),
					},
				});
			} catch (error) {
				if (error instanceof ApiError && isChannelNotFoundError(error)) {
					return {
						error: error.message,
					};
				}

				throw error;
			}
		},
		[updateConversation],
	);

	const onConversationDelete = useCallback(
		async (conversationId: string) => {
			try {
				await deleteConversation({
					conversationId,
				});
			} catch (error) {
				if (error instanceof ApiError && isChannelNotFoundError(error)) {
					return {
						error: error.message,
					};
				}

				throw error;
			}
		},
		[deleteConversation],
	);

	const onConversationPinToggle = useCallback(
		async (conversationId: string, isPinned: boolean) => {
			await updateConversation({
				conversationId,
				data: {
					isPinned,
				},
			});
		},
		[updateConversation],
	);

	return (
		<ChatConversationListRefresh
			title={formatMessage(messages.titleLabel)}
			searchPlaceholderText={formatMessage(messages.searchInputPlaceholderLabel)}
			conversations={conversations}
			searchQuery={searchQuery}
			onNewChatClick={onNewChatClick}
			onSearchQueryChange={setSearchQuery}
			onConversationSelect={onConversationSelect}
			onConversationDelete={onConversationDelete}
			onConversationEdit={onConversationEdit}
			onConversationPinToggle={isPinnedChatsEnabled ? onConversationPinToggle : undefined}
			isSpinnerOverlayConversationIconEnabled={fg('rovo_chat_chat_history_refresh')}
			isFullScreen={!!fullScreen}
			chatRefreshHeader={chatRefreshHeader}
			cloudId={siteId}
			hasMoreConversations={conversationPagination.hasMore}
			isLoadingMoreConversations={conversationPagination.isLoadingMore}
			hasLoadMoreConversationsError={Boolean(conversationPagination.loadMoreError)}
			onLoadMoreConversations={loadMoreConversationChannelsData}
		/>
	);
};
